import React, { Fragment, useCallback, useContext, useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import overlayFactory from 'react-bootstrap-table2-overlay';
import paginationFactory from 'react-bootstrap-table2-paginator';
import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css';
import { useTranslation } from 'react-i18next';
import { NavLink, useHistory } from 'react-router-dom';
import { changePatientNumber, getVisitOffsetOverrides, getVisitsTracking, removePatient, saveVisitOffsetOverride } from '../../../api/protocol';
import '../../../assets/sass/lbd/_visits-tracking.scss';
import { CreateAuditModal } from '../../../components/AuditTrail/AuditModal';
import { WithAuth } from '../../../components/Authorization/WithAuthProtocol';
import Card from '../../../components/Cards/Card';
import { CustomSearchBox } from '../../../components/CustomControls/CustomSearchBox';
import { Add, Check, Edit, Refer, Remove } from '../../../components/Icons/Icons';
import { HandleApiError } from '../../../components/Notifications/APIErrorHandler';
import { PatientStatusInProtocol } from '../../../components/Protocols/Patients';
import { VisitTrackingStageFilter, VisitTrackingStatusFilter, VisitTrackingBranchFilter } from '../../../components/Protocols/Tracking/Filters';
import { AddPatientModal } from '../Patients/AddPatients';
import { PatientNumberModal } from '../Patients/PatientNumber';
import ProtocolContext from '../ProtocolContext/ProtocolContext';
import { getVisitsTrackingColumns, getVisitsTrackingGrid } from './Services';
import { ModalConfirm } from '../../../components/Modals/ModalConfirm';
import { ReferPatientModal } from '../Patients/ReferPatient';
import { Tooltip } from '../../../components/Utils/Tooltips';
import { VisitOffsetEditModal } from './VisitOffsetEditModal';
import { AssignBranchModal, BranchInterceptModal, useBranches } from '../Branches';

export const VisitsTrackingContainer = props => {
    const { t } = useTranslation();
    const { protocolId } = props;
    let history = useHistory();
    const context = useContext(ProtocolContext);

    const [protocolBranches] = useBranches(protocolId);
    const [branchIntercept, setBranchIntercept] = useState(null);

    const handleVisitClick = useCallback((crossVersionId, numberInProtocol, _cell, row, visitTemplate) => {
        const visitRequiresBranch = visitTemplate?.branches?.length > 0;
        const patientHasBranch = !!row.branch;

        if (visitRequiresBranch && !patientHasBranch) {
            setBranchIntercept({ crossVersionId, numberInProtocol });
            return;
        }
        history.push(`/admin/protocols/${protocolId}/visitTracking/${crossVersionId}/patient/${numberInProtocol}`);
    }, [history, protocolId]);

    const firstColumns = [
        {
            dataField: 'numberInProtocol', text: t('protocols.patient.nro'), sort: true, headerStyle: { width: '150px' }, formatter: function (cell, row) {
                return <>
                    {cell}
                    <WithAuth protocolId={protocolId} requiredPermission={["Patients.Edit"]}>
                        <Tooltip tooltip={t('protocols.patient.changePatientNumber')} id={"changePatientNumber-" + row.numberInProtocol}>
                            <button onClick={() => handlePatientNumberChange(row)} className="btn-table-actions" title={t('protocols.patient.changePatientNumber')} type='button'>
                                <Edit style={{ height: '15px', width: '15px' }} />
                            </button>
                        </Tooltip>
                    </WithAuth>
                    <WithAuth protocolId={protocolId} requiredPermission={["Patients.Refer"]}>
                        <Tooltip tooltip={t('protocols.patient.referPatient')} id={"referPatient-" + row.numberInProtocol}>
                            <button onClick={() => handleReferPatient(row)} className="btn-table-actions" title={t('protocols.patient.referPatient')} type='button'>
                                <Refer style={{ height: '15px', width: '15px' }} />
                            </button>
                        </Tooltip>
                    </WithAuth>
                    {
                        row.visits && row.visits.length === 0 &&
                        <WithAuth protocolId={protocolId} requiredPermission={["Patients.Delete"]}>
                            <button onClick={() => handleDeletePatient(row)} className="btn-remove btn-table-actions" type='button'>
                                <Remove style={{ height: '15px', width: '15px' }} />
                            </button>
                        </WithAuth>
                    }
                    {
                        protocolBranches.length > 0 && !row.branch &&
                        <WithAuth protocolId={protocolId} requiredPermission={["VisitTracking.Add"]}>
                            <Tooltip tooltip={t('protocols.branches.assignBranch')} id={"assignBranch-" + row.numberInProtocol}>
                                <button
                                    onClick={() => setModal(
                                        <AssignBranchModal
                                            isShowing={true}
                                            hide={() => setModal(null)}
                                            protocolId={protocolId}
                                            numberInProtocol={row.numberInProtocol}
                                            isChange={false}
                                            onSuccess={() => { setModal(null); setReload(true); }}
                                        />
                                    )}
                                    className="btn-table-actions"
                                    type='button'>
                                    <Check style={{ height: '15px', width: '15px' }} />
                                </button>
                            </Tooltip>
                        </WithAuth>
                    }
                </>;
            }
        },
        {
            dataField: 'fullName', text: 'Int.', sort: true, headerStyle: { width: '150px' }, formatter: function (cell, row, rowIndex) {
                return <NavLink to={`/admin/medicalRecords/${row.medicalRecordNumber}`} key={`patient-${rowIndex}`}>
                    {cell}
                </NavLink>
            }
        },
        {
            dataField: 'statusInProtocol', text: 'Status', headerStyle: { width: '150px' }, formatter: function (cell, _row, rowIndex) {
                return (<PatientStatusInProtocol key={`statusrow-${rowIndex}`} status={cell} />)
            }
        },
    ];

    const [isLoading, setIsLoading] = useState(false);
    const [modal, setModal] = useState(null);
    const [reload, setReload] = useState(false);
    const [columns, setColumns] = useState(firstColumns);
    const [selectedStage, setSelectedStage] = useState(null);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [data, setData] = useState([]);
    const [totalSize, setTotalSize] = useState(0);
    const [query, setQuery] = useState({
        pagination: {
            page: 1,
            sizePerPage: 5,
            totalSize: 0
        },
        sort: {
            sortField: 'numberinprotocol',
            //sortOrder: 'desc'
        },
        filters: {}
    });

    const [visitTemplates, setVisitTemplates] = useState([]);
    const [patientsData, setPatientsData] = useState([]);
    const [visitOverrides, setVisitOverrides] = useState({});
    const [editingOffsetContext, setEditingOffsetContext] = useState(null);

    const getOverrideKey = (numberInProtocol, visitCrossVersionId) => `${numberInProtocol}_${visitCrossVersionId}`;
    useEffect(() => {
        const fetchData = () => {
            setReload(false);
            setIsLoading(true);
            Promise.all([
                getVisitsTracking(protocolId, query),
                getVisitOffsetOverrides(protocolId).catch(() => ({ data: [] }))
            ])
                .then(([response, overrideResponse]) => {
                    var visitTracking = response.data
                    //console.log(response.data)
                    let visits = visitTracking.visits;
                    setVisitTemplates(visits);
                    const overrideMap = (overrideResponse?.data || []).reduce((acc, item) => {
                        const key = getOverrideKey(item.patientId, item.visitCrossVersionId);
                        acc[key] = item;
                        return acc;
                    }, {});
                    setVisitOverrides(overrideMap);
                    let patients = [];

                    const currentPatient = new URLSearchParams(props.location.search).get("patient");
                    if (currentPatient) {
                        patients = visitTracking.patients.data.filter(patient => (patient.medicalRecordNumber === currentPatient));
                    } else {
                        patients = visitTracking.patients.data;
                    }

                    setPatientsData(patients);

                    // Armo columnas a mostrar.
                    const cols = getVisitsTrackingColumns(protocolId, visits, history, t, handleEditOffset);
                    let allcolumns = firstColumns.concat(cols);
                    setColumns(allcolumns);
                    //console.log(allcolumns)
                    // Armo grilla
                    var data = getVisitsTrackingGrid(patients, visits, overrideMap);
                    setTotalSize(visitTracking.patients.count);
                    setData(data);
                    //console.log(data)
                    setIsLoading(false);
                })
                .catch(error => {
                    console.log(error);
                    HandleApiError(error);
                    setIsLoading(false);
                })
        }

        fetchData();
    }, [protocolId, query, reload]);

    const handleTableChange = (type, { page, sizePerPage, sortField, sortOrder }) => {

        if (type === 'pagination') {
            let newQuery = JSON.parse(JSON.stringify(query));

            newQuery.pagination.page = page;
            newQuery.pagination.sizePerPage = sizePerPage;

            setQuery(newQuery);
        }
        if (type === 'sort') {
            let newQuery = JSON.parse(JSON.stringify(query));

            newQuery.sort.sortField = sortField;
            newQuery.sort.sortOrder = sortOrder;

            setQuery(newQuery);
        }
    }

    const handleFilterChange = (filterName, selectedValue) => {
        if (filterName) {

            if (filterName === "patientNumber") {
                var newQuery = JSON.parse(JSON.stringify(query));
                newQuery.filters[filterName] = selectedValue;
                setQuery(newQuery);
            }
            if (filterName === "status") {
                var newQuery = JSON.parse(JSON.stringify(query));
                newQuery.filters[filterName] = selectedValue != null ? selectedValue.id : selectedValue;
                setQuery(newQuery);
            }
            if (filterName === "branchId") {
                setSelectedBranch(selectedValue);
                var newQuery = JSON.parse(JSON.stringify(query));
                newQuery.filters[filterName] = selectedValue != null ? selectedValue.id : null;
                setQuery(newQuery);
            }
            if (filterName === "stage") {
                setSelectedStage(selectedValue);

                let filteredVisits = [];
                if (selectedValue != null)
                    filteredVisits = visitTemplates.filter(x => x.stage === selectedValue.id);
                else
                    filteredVisits = visitTemplates;


                // Armo columnas a mostrar.
                const cols = getVisitsTrackingColumns(protocolId, filteredVisits, history, t, handleEditOffset);
                let allcolumns = firstColumns.concat(cols);
                setColumns(allcolumns);
                // Armo grilla
                var data = getVisitsTrackingGrid(patientsData, filteredVisits, visitOverrides);
                setTotalSize(patientsData.count);
                setData(data);
            }
        }
    }

    const handleEditOffset = (cell, row, visit) => {
        if (!cell?.isTemplateVisit) return;
        setEditingOffsetContext({ cell, row, visit });
    }

    const handleCloseEditOffset = () => {
        setEditingOffsetContext(null);
    }

    const handleSaveOffsetOverride = async (override) => {
        try {
            const key = getOverrideKey(override.patientId, override.visitCrossVersionId);
            const nextOverrides = { ...visitOverrides, [key]: override };
            setVisitOverrides(nextOverrides);

            // Optimistic recalculation in grid
            const activeVisits = selectedStage != null
                ? visitTemplates.filter(x => x.stage === selectedStage.id)
                : visitTemplates;
            const refreshedData = getVisitsTrackingGrid(patientsData, activeVisits, nextOverrides);
            setData(refreshedData);

            await saveVisitOffsetOverride(protocolId, override);
            setEditingOffsetContext(null);
        }
        catch (error) {
            HandleApiError(error);
        }
    }

    const handleAddPatient = () => {
        setModal(<AddPatientModal
            protocolId={protocolId}
            hide={() => setModal(null)}
            submit={() => { setReload(true); setModal(null) }}
            isShowing={true} />);
    }

    const handlePatientNumberChange = (patient) => {
        setModal(<PatientNumberModal
            hide={() => setModal(null)}
            submit={(value) => { handleAuditChange(value) }}
            isShowing={true}
            patient={patient}
        />);
    }

    const handleReferPatient = (patient) => {
        setModal(<ReferPatientModal
            hide={() => setModal(null)}
            submit={(value) => { setReload(true); setModal(null); }}
            isShowing={true}
            patient={patient}
            protocolId={protocolId}
        />);
    }

    const handleAuditChange = (value) => {
        setModal(<CreateAuditModal
            onClose={() => setModal(null)}
            onSave={(reason, comments) => confirmChangePatientNumberSubmit(value, reason, comments)}
        />)
    }

    const confirmChangePatientNumberSubmit = async (patient, reason, comments) => {
        try {
            patient.audit = {
                reason: reason,
                comments: comments
            }
            await changePatientNumber(protocolId, patient.medicalRecordNumber, patient);
            setModal(null);
            setReload(true);
        }
        catch (error) {
            console.log(error);
            HandleApiError(error);
            setModal(null)
        }
    }

    const handleDeletePatient = (patient) => {
        setModal(<ModalConfirm
            title={`${t('protocols.patient.patient')}: ${patient.fullName}. ${t('protocols.patient.nro')}: ${patient.numberInProtocol}`}
            description={t('protocols.patient.removePatient_description')}
            onCancel={() => setModal(null)}
            onConfirm={() => { confirmDeletePatient(patient) }}
        />);
    }

    const confirmDeletePatient = async (patient) => {
        try {
            await removePatient(protocolId, patient.numberInProtocol);
            setModal(null);
            setReload(true);
        }
        catch (error) {
            console.log(error);
            HandleApiError(error);
            setModal(null)
        }
    }

    return (
        <>
            {branchIntercept && (
                <BranchInterceptModal
                    isShowing={true}
                    protocolId={protocolId}
                    numberInProtocol={branchIntercept.numberInProtocol}
                    onSuccess={(branchId) => {
                        setBranchIntercept(null);
                        setReload(true);
                        history.push(`/admin/protocols/${protocolId}/visitTracking/${branchIntercept.crossVersionId}/patient/${branchIntercept.numberInProtocol}`);
                    }}
                    onCancel={() => setBranchIntercept(null)}
                />
            )}
            <Card
                header={
                    <Fragment>
                        <Row>
                            <WithAuth protocolId={protocolId} requiredPermission={["Patients.Add"]}>
                                <Col md={2}>
                                    <button onClick={handleAddPatient} className="btn btn-primary" disabled={context.isDisabled}>
                                        <Add /> {t('protocols.patient.addPatientTitle')}
                                    </button>
                                </Col>
                            </WithAuth>
                            <Col md={2}>
                                <CustomSearchBox handleSearch={(value) => handleFilterChange("patientNumber", value)} placeholder={t('protocols.visitTracking.searchByNumber')}></CustomSearchBox>
                            </Col>
                            <Col md={2}>
                                <VisitTrackingStatusFilter onChangeFilter={handleFilterChange} />
                            </Col>
                            <Col md={2}>
                                <VisitTrackingStageFilter onChangeFilter={handleFilterChange} selectedStage={selectedStage} />
                            </Col>
                            {protocolBranches.length > 0 && (
                                <Col md={2}>
                                    <VisitTrackingBranchFilter
                                        onChangeFilter={handleFilterChange}
                                        selectedBranch={selectedBranch}
                                        branches={protocolBranches}
                                    />
                                </Col>
                            )}
                        </Row>
                    </Fragment>
                }
                content={
                    <>
                        {modal}
                        {
                            editingOffsetContext &&
                            <VisitOffsetEditModal
                                isOpen={!!editingOffsetContext}
                                toggle={handleCloseEditOffset}
                                visit={editingOffsetContext.visit}
                                patient={editingOffsetContext.row}
                                allVisits={visitTemplates}
                                currentOverride={visitOverrides[getOverrideKey(editingOffsetContext.row.numberInProtocol, editingOffsetContext.visit.crossVersionId)]}
                                onSave={handleSaveOffsetOverride}
                            />
                        }

                        <BootstrapTable
                            classes="table-spaced table-spaced-small"
                            wrapperClasses="table-responsive visit-tracking-table"
                            bordered={false}
                            hover
                            striped
                            condensed
                            remote
                            keyField='medicalRecordNumber'
                            data={data}
                            columns={columns}
                            pagination={paginationFactory({
                                page: query.pagination.page,
                                sizePerPage: query.pagination.sizePerPage,
                                totalSize: totalSize,
                                hideSizePerPage: true,
                            })}

                            onTableChange={handleTableChange}
                            noDataIndication={() => <>{t("commons.noData")}</>}
                            loading={isLoading}  //only loading is true, react-bootstrap-table will render overlay
                            overlay={overlayFactory({ spinner: true })}
                        />
                    </>
                }
            />
        </>
    );
}